// React
import { useState } from "react"

// PWA
import { useRegisterSW } from "virtual:pwa-register/react"

// Images
import Icon from "@/assets/icons/brand/logo.svg"

// UI
import { Button } from "../Button"

/**
 * ReloadPrompt
 *
 * A component that displays a toast when a new version of the app is available.
 *
 * @returns {JSX.Element}
 */
export function ReloadPrompt() {
	const intervalMS = 10 * 1000 // 10 seconds
	const [isUpdating, setIsUpdating] = useState(false)

	const {
		updateServiceWorker,
		needRefresh: [needRefresh, setNeedRefresh],
	} = useRegisterSW({
		onRegistered(r) {
			console.log("SW: Registered worker:", r)

			// If the worker is active, set an interval to check for updates
			if (r && r.active) {
				setInterval(async () => {
					console.log("SW: Checking for update")
					try {
						await r.update()
						console.log("SW: Update check completed")
					} catch (error) {
						console.error("SW: Update check failed", error)
					}
				}, intervalMS)
			}
		},
		onNeedRefresh() {
			console.log("SW: Need refresh")
			setNeedRefresh(true)
		},
		onRegisterError(error) {
			console.error("SW: error", error)
		},
		onOfflineReady() {
			console.log("SW: Offline ready")
		},
	})

	/**
	 * Reload the app with proper service worker update and cache clearing
	 */
	async function reloadApp() {
		if (isUpdating) return // Prevent multiple clicks

		try {
			console.log("SW: Starting update process...")
			setIsUpdating(true)
			setNeedRefresh(false)

			// Step 1: Update the service worker
			console.log("SW: Updating service worker...")
			await updateServiceWorker(true)
			console.log("SW: Service worker updated successfully")

			// Step 2: Clear all caches
			console.log("SW: Clearing all caches...")
			if ("caches" in window) {
				const cacheNames = await caches.keys()
				await Promise.all(
					cacheNames.map(async (cacheName) => {
						console.log(`SW: Deleting cache: ${cacheName}`)
						return caches.delete(cacheName)
					}),
				)
				console.log("SW: All caches cleared successfully")
			} else {
				console.log("SW: Cache API not supported")
			}

			// Step 4: Force reload the page
			console.log("SW: Reloading page...")
			window.location.reload()
		} catch (error) {
			console.error("SW: Update process failed:", error)
			setIsUpdating(false)
			// Fallback: just reload the page
			window.location.reload()
		}
	}

	// If the app needs a refresh, show the reload prompt
	if (needRefresh) {
		return (
			<div className="absolute right-0 top-0 z-[100] m-0 h-0 w-auto p-4">
				<div className="flex gap-4 rounded border border-[#8885] bg-white p-3 shadow-[3px_4px_5px_0_#8885]">
					<img
						src={Icon}
						alt=""
						className="animate-spin-slow"
						width="30"
						height="30"
					/>
					<div className="ml-auto flex flex-col justify-center">
						<Button
							onClick={() => reloadApp()}
							disabled={isUpdating}
							className={
								isUpdating
									? "cursor-not-allowed opacity-50"
									: ""
							}
						>
							{isUpdating ? "Updating..." : "Update App"}
						</Button>
					</div>
				</div>
			</div>
		)
	}

	// Default: do not show anything
	return null
}
